import { DraftOnly } from "@components/project/DraftModeRequired.component";
import { useEntitlements } from "@frontegg/react";
import { PipelineNodeORM } from "@models/pipelineNode";
import { PageContentHeader } from "@pages/PageStructure.component";
import { getPromptAnswer, requireConfirmation } from "@services/alert/alert.service";
import { summarizeNumber } from "@services/formatting.service";
import { getNodeTypeConfig } from "@services/modeling.service";
import { timeAgo } from "@services/time.service";
import { useIsInDraftMode, usePipelineNode, useTemplateName, useIsFavoriteNode, toggleFavoriteNode, invalidateEverything } from "@stores/data.store";
import { ReactNode, useMemo, useCallback, useState, useEffect, KeyboardEvent } from "react";
import { Badge, Dropdown, Modal, Form } from "react-bootstrap";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import dbtIcon from '@assets/images/dbt.png';
import FavoriteButton from "@components/button/FavoriteButton.component";
import SaveButton from "@components/button/SaveButton.component";
import useGlobalState from "@stores/global.state";
import toast from "@services/toast.service";
import { getErrorMessage } from "@services/errors.service";
import LoadingCard from "@components/card/LoadingCard.component";
import BuildNodeModal from "@components/pipelineNodes/modal/BuildNodeModal.component";
import { triggerBuild } from "@services/alert/BuildModal.component";
interface Props {
    pipelineNodeId: string;
    children?: ReactNode;
    enableSearch?: boolean;
    showSaveButton?: boolean;
    onSave?: () => void | Promise<void>;
    saveDisabled?: boolean;
}

const PipelineNodeSubnav = (props: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const pipelineNode = usePipelineNode(props.pipelineNodeId);
    const inDraftMode = useIsInDraftMode();
    const isFavorite = useIsFavoriteNode(props.pipelineNodeId);

    const query = searchParams.get('q') || '';
    const [searchInput, setSearchInput] = useState(query);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [downloadLocation, setDownloadLocation] = useState('');
    const [downloadError, setDownloadError] = useState('');
    const [showBuildNodeModal, setShowBuildNodeModal] = useState(false);

    useEffect(() => {
        setSearchInput(query);
    }, [query]);

    const handleFavoriteClick = useCallback(async () => {
        await toggleFavoriteNode(props.pipelineNodeId);
    }, [props.pipelineNodeId]);

    const activePage = useMemo(() => {
        let path = location.pathname;

        if (path.endsWith('/')) {
            path = path.substring(0, path.length - 1);
        }
        
        if (path.endsWith('/config')) {
            return 'config';
        } else if (path.endsWith('/files')) {
            return 'files';
        } else if (path.indexOf('/relationships') >= 0) {
            return 'relationships';
        } else if (path.endsWith('/insights')) {
            return 'insights';
        } else if (path.endsWith('/filters')) {
            return 'filters';
        } else if (path.indexOf('/visualizations') >= 0) {
            return 'visualizations';
        }

        return 'data';
    }, [location]);

    const nodeConfig = useMemo(() => {
        if (!pipelineNode.data) {
            return undefined;
        }
        return getNodeTypeConfig(pipelineNode.data);
    }, [pipelineNode.dataUpdatedAt]);

    const nodeTypeConfig = useMemo(() => {
        if (!pipelineNode.data) {
            return undefined;
        }

        return getNodeTypeConfig(pipelineNode.data);
    }, [pipelineNode.dataUpdatedAt]);
    
    const currentNodeType = pipelineNode.data?.node_type;

    const copyNode = useCallback(async () => {
        const newLabel = await getPromptAnswer('Enter new name for the copied node', 'Copy Node', false, `${pipelineNode.data?.label} copy`);
        if (!newLabel) {
            return;
        }
        const newNode = await PipelineNodeORM.copyPipelineNode(props.pipelineNodeId, newLabel);
        invalidateEverything();
        navigate(`/node/${newNode.id}/config`);
    }, [props.pipelineNodeId, pipelineNode.dataUpdatedAt]);

    const { setPageDirty } = useGlobalState();

    const deleteNode = useCallback(async () => {
        const confirmed = await requireConfirmation(`Are you sure you want to delete this node? Type out the full node name ("${pipelineNode.data?.label}") to continue.`, 'Delete Node', 'Delete', 'Cancel', pipelineNode.data?.label);
        if (confirmed) {
            setPageDirty(false);
            await PipelineNodeORM.deleteById(props.pipelineNodeId);
            navigate('/');
            invalidateEverything();
            toast('success', 'Node deleted', 'Node deleted successfully.');
        }
    }, [props.pipelineNodeId, pipelineNode.dataUpdatedAt]);

    const downloadNodeData = useCallback(async () => {
        if (!pipelineNode.data) {
            return;
        }
        setDownloadError('');
        setShowDownloadModal(true);
        setLoadingDownload(true);
        setDownloadLocation('');

        try {
            const download = await PipelineNodeORM.queueDownloadJob(props.pipelineNodeId, searchInput || '');
            setDownloadLocation(download.url);
            
        } catch(err_msg) {
            setDownloadError(getErrorMessage(err_msg));
            console.log(err_msg)
        } finally {
            setLoadingDownload(false);
        }
        
    }, [pipelineNode.data, props.pipelineNodeId, searchInput]);

    const handleSearchKeyDown = useCallback((evt: KeyboardEvent<HTMLInputElement>) => {
        if (evt.key === 'Enter') {
            setSearchParams({q: searchInput});
        }
    }, [searchInput, setSearchParams]);

    const handleRefreshClick = useCallback(async () => {
        // Only check for unsaved changes if we're on the config page (where showSaveButton is true)
        // Check if page is dirty (has unsaved changes)
        const isPageDirty = props.showSaveButton && !props.saveDisabled;
        
        if (isPageDirty && props.onSave) {
            const confirmed = await requireConfirmation(
                'You have unsaved changes which may affect your build. Would you like to save before refreshing?',
                'Warning: Unsaved Changes',
                'Save & Refresh',
                'Cancel'
            );
            if (!confirmed) {
                return;
            }
            // Save first, then show the build modal
            await props.onSave();
        }
        
        // Show the build modal after saving (or if no unsaved changes)
        setShowBuildNodeModal(true);
    }, [props.showSaveButton, props.saveDisabled, props.onSave]);

    
    
   
    
    const badge = useMemo(() => {
        if (!pipelineNode.data || !nodeTypeConfig) {
            return <></>
        }

        const bg = nodeTypeConfig.group.color;
        const icon = pipelineNode.data.flat_file ? 'mdi mdi-file-document' : nodeTypeConfig.nodeType.icon;
        const label = pipelineNode.data.flat_file ? 'Flat File' : nodeTypeConfig.nodeType.label;


        return (
            <Badge
                pill
                bg={bg}
            ><i className={icon}></i> {label}
            </Badge>
        );
    }, [pipelineNode.data, nodeTypeConfig]);

    const templateName = useTemplateName(pipelineNode.data?.managed_by_template_id || '') || undefined;

    if (!pipelineNode.data || !nodeConfig) {
        return <></>;   
    }

    return <>
        <BuildNodeModal 
            show={showBuildNodeModal} 
            onClose={() => setShowBuildNodeModal(false)}
            defaultCheck={pipelineNode.data?.node_type == 'SOURCE'}
            defaultUpstream={pipelineNode.data?.node_type != 'SOURCE'}
            header={`Refresh Data: ${pipelineNode.data?.label || ''}`}
            onConfirm={(selector: "THIS" | "UPSTREAM" | "DOWNSTREAM" | "ALL", force: boolean, check_for_records: boolean) => {triggerBuild(pipelineNode.data!, selector, force, check_for_records, () => {
                navigate(`/node/${props.pipelineNodeId}`);
            })}}
        />
        <Modal show={showDownloadModal} onHide={() => setShowDownloadModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Download</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
                {loadingDownload && (
                    <div>
                        <LoadingCard action="Preparing your download" />
                    </div>
                )}
                {!loadingDownload && downloadError && (
                    <div className="alert alert-danger">{downloadError}</div>
                )}
                {!loadingDownload && downloadLocation && !downloadError && (
                    <div>
                        <span style={{ fontSize: 55 }}><i className="mdi mdi mdi-download"></i></span>
                        <br />
                        <a target="_blank" className="btn btn-pliable" href={downloadLocation} rel="noreferrer">Click here to download your file.</a>
                    </div>
                )}
            </Modal.Body>
        </Modal>
        <PageContentHeader>
            <div className="d-flex center-vertically" style={{height: '100%'}}>
                <FavoriteButton
                    isFavorite={isFavorite}
                    onToggle={handleFavoriteClick}
                    className="me-2"
                />
                <div className="flex-1 overflow-ellipsis">
                    
                    <h1 className="mb-0 overflow-ellipsis" style={{ maxWidth: '40vw' }} title={pipelineNode.data?.label}>
                        {pipelineNode.data?.label}
                    </h1>
                    <div className="font-poppins text-muted font-13">
                    {pipelineNode.data?.source_id && <>
                        <span role="button" onClick={() => navigate(`/source/${pipelineNode.data?.source_id}`)}>{badge}</span>
                    </>}
                    {!pipelineNode.data?.source_id && <>
                        <span>{badge}</span>
                    </>}
                        <span className="me-3">&nbsp;</span>
                        <span>
                            <i className="mdi mdi-file-multiple"></i> Records: {pipelineNode.data.total_records ? summarizeNumber(pipelineNode.data.total_records) : '0'}
                        </span>
                        <span className="me-3">&nbsp;</span>
                        <span>
                            <i className="mdi mdi-clock"></i> Last Updated: {pipelineNode.data.last_build_completed ? timeAgo(pipelineNode.data.last_build_completed, 'mini-minute-now') : 'never'}
                        </span>
                        <span className="me-3">&nbsp;</span>
                        <span title={templateName}>
                            {pipelineNode.data.managed_by_template_id && (
                                <i className="mdi mdi-image-frame"></i>
                            )}
                        </span>
                    </div>
                </div>
                {props.enableSearch && (
                    <div className="me-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            style={{ minWidth: '200px' }}
                        />
                    </div>
                )}
                <div className="btn-group btn-light">
                    
                    <button className="btn btn-light" onClick={downloadNodeData} title="Download Data">
                        <i className="mdi mdi-download"></i>
                    </button>
                    <button className="btn btn-light" title="Refresh Data" onClick={handleRefreshClick}>
                        <i className="mdi mdi-refresh"></i>
                    </button>
                    
                    <Dropdown>
                        <Dropdown.Toggle
                            variant="light"
                            className="nocaret ps-1 pe-1"
                            style={{
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                borderLeft: 0,
                            }}
                        >
                            <i className="mdi mdi-dots-vertical"></i>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item onClick={copyNode}>
                                <i className="mdi mdi-content-copy"></i> Copy Node
                            </Dropdown.Item>
                            <Dropdown.Item onClick={deleteNode}>
                                <i className="mdi mdi-delete"></i> Delete Node
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                    {props.showSaveButton && props.onSave && (
                        <div className="ms-3">
                            <SaveButton onClick={props.onSave} disabled={props.saveDisabled} />
                        </div>
                    )}
                <div className="ms-3 btn-group">
                    <Link to={`/node/${props.pipelineNodeId}`} className={`btn btn-light ${activePage == 'data' ? 'active' : ''}`}>
                        <i className="mdi mdi-table"></i> View Data
                    </Link>
                    
                    {pipelineNode.data?.flat_file && <>
                        <Link to={`/node/${props.pipelineNodeId}/files`} className={`btn btn-light ${activePage == 'files' ? 'active' : ''}`}>
                                <i className="mdi mdi-file-multiple"></i> Files
                            </Link>
                    </>}
                
                    <Link to={`/node/${props.pipelineNodeId}/config`} className={`btn btn-light ${activePage == 'config' ? 'active' : ''}`}>
                        <i className="mdi mdi-cog"></i> Configure
                    </Link>
                </div>
                
            </div>
        
        
    </PageContentHeader>
    </>;
}

export default PipelineNodeSubnav;
